<template>
    <ion-page>
        <ion-content :force-overscroll="!isWeb">
            <PopUpMask :style="titleFont">
                <template #icon>
                    <img :src="icon" />
                </template>

                <template #title>
                    <h2 id="title" :style="titleFont">
                        {{ labels.title }}
                    </h2>
                </template>

                <template #description>
                    <div class="description-wrapper">
                        <span v-for="(line, key) in labels.lines" :key="key" v-html="line"></span>
                    </div>

                    <MilageInput v-model:value="videoUrl" :label="labels.videoLabel"
                        :placeholder="labels.videoPlaceholder" type="text" :is-warning="isWarning" />

                    <div class="zoom-toolbar" >
                        <input type="range" :min="20" :max="100" :step="5" :value="widthPercent" @input="onWidthInput"
                            class="ball-slider" />
                        <span class="zoom-label">{{ widthPercent }}%</span>
                    </div>


                    <ErrorMsg v-if="isWarning" :msg="labels.invalidVideoError" />
                </template>

                <template #button>
                    <div style="width: 100%;">
                        <IonButton height="2.575241849rem" @click="confirm">
                            {{ labels.btn }}
                        </IonButton>
                    </div>
                </template>
            </PopUpMask>
        </ion-content>
    </ion-page>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, computed, toRefs } from "vue";
import { IonContent, IonPage, IonButton, modalController } from "@ionic/vue";

import PopUpMask from "@/modules/a.worksheets/views/components/feedback/PopUpMask.vue";
import MilageInput from "@/globals/components/MilageInput.vue";
import ErrorMsg from "@/globals/components/ErrorMsg.vue";

import { getFont, IFont } from "@/helpers/fonts";
import { useDeviceStatus } from "@/stores/deviceStatus";

const props = defineProps<{
    initialUrl?: string;
    iconSrc?: string;
    labels?: Partial<{
        title: string;
        lines: string[];
        videoLabel: string;
        videoPlaceholder: string;
        btn: string;
        invalidVideoError: string;
    }>;
}>();

const { isWeb } = toRefs(useDeviceStatus());

const icon = ref<string>("");
const titleFontStyle = ref<IFont>();

const videoUrl = ref<string>(props.initialUrl ?? "");
const isWarning = ref(false);



const widthPercent = ref<number>(80);


function onWidthInput(e: Event) {
    const el = e.target as HTMLInputElement;
    const raw = Number(el.value);

    const snapped = Math.round(raw / 10) * 10;

    const clamped = Math.min(100, Math.max(20, snapped));

    widthPercent.value = clamped;

    el.value = String(clamped);
}

const labels = computed(() => ({
    title: props.labels?.title ?? "Inserir vídeo",
    lines: props.labels?.lines ?? [
        "Cola o link do vídeo (YouTube, Vimeo, etc.).",
    ],
    videoLabel: props.labels?.videoLabel ?? "Vídeo",
    videoPlaceholder: props.labels?.videoPlaceholder ?? "https://...",
    btn: props.labels?.btn ?? "Inserir",
    invalidVideoError: props.labels?.invalidVideoError ?? "URL inválida.",


    widthLabel: (props.labels as any)?.widthLabel ?? "Largura do vídeo",
}));

onBeforeMount(async () => {
    titleFontStyle.value = await getFont("nobo25");

    icon.value =
        props.iconSrc ??
        "data:image/svg+xml;utf8," +
        encodeURIComponent(`
        <svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 24 24' fill='none' stroke='#2563eb' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'>
          <rect x='3' y='5' width='18' height='14' rx='2' ry='2'/>
          <polygon points='10 9 16 12 10 15 10 9'/>
        </svg>
      `);
});

const titleFont = computed(() => ({
    "--title-font-family": titleFontStyle.value?.["font-family"],
    "--title-font-size": titleFontStyle.value?.["font-size"],
    "--title-font-transform": titleFontStyle.value?.["text-transform"],
    "--title-font-weight": titleFontStyle.value?.["font-weight"],
}));

function isValidBasicUrl(u: string) {
    try {
        const parsed = new URL(u);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
        return false;
    }
}

function confirm() {
    const value = videoUrl.value.trim();

    if (!value) {
        isWarning.value = true;
        return;
    }

    if (!isValidBasicUrl(value) && !/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value)) {
        isWarning.value = true;
        return;
    }

    isWarning.value = false;

    modalController.dismiss(
        { url: value, width: `${widthPercent.value}%` },
        "confirm"
    );
}
</script>

<style scoped>
ion-content::part(background) {
    background-color: transparent;
}

#title {
    font-family: var(--title-font-family);
    font-size: var(--title-font-size);
    text-transform: var(--title-font-transform);
    font-weight: var(--title-font-weight);
    text-align: center;
}

img {
    width: 100%;
}

.description-wrapper {
    font-family: var(--description-font-family);
    font-size: var(--description-font-size);
    text-transform: var(--description-font-transform);
    font-weight: var(--description-font-weight);
    color: var(--milage-color-default-body);
}

.description-wrapper span {
    display: block;
    text-align: center;
}

ion-button {
    width: 100%;
}

.zoom-toolbar{
  margin-top: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}


.ball-slider{
  width: 180px; 
  max-width: 60vw;
}


.zoom-label{
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 4ch; 
  text-align: center;

  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}
</style>
